import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button, CheckBox } from "components/atoms";
import {
  AddressInput,
  TextInput,
  EmailInput,
  GeneralSelectInput,
  DatePickerInput,
  DocumentUploadModal,
  YearPickerInput,
  PhoneNumberInput,
  RadioButtonGroup,
  MultiChoiceRadioButtonGroup,
  MessageTextAreaInput,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import {
  documentService,
  createPostalLookupService,
  shouldShowAddressState,
  formatPostalCode,
} from "services";
import {
  ApplicationFormValues,
  titleOptions,
  genderOptions,
  maritalStatusOptions,
  countriesFlat,
  countryCodes,
  visaTypeOptions,
  applicationTypeOptions,
  FileWithType,
} from "types";
import "./AppPersonalSection.scss";

interface AppPersonalSectionProps {
  currentSubStage?: string;
  destinationCountry?: string;
}

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set([
    "personalInformation",
    "englishLanguageTestScores",
    "highSchool",
    "tertiaryEducation1",
    "tertiaryEducation2",
    "tertiaryEducation3",
    "educationDetails",
    "scholarshipFinancialSupport",
    "healthInsurance",
    "studentSupportServices",
    "employment1",
    "employment2",
    "employment3",
    "employmentDetails",
  ]),
};

const AppPersonalSection: React.FC<AppPersonalSectionProps> = ({
  currentSubStage = "basic",
  destinationCountry = "Australia",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setTouched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ApplicationFormValues>();

  const [lookupTimeouts, setLookupTimeouts] = useState<
    Record<string, NodeJS.Timeout>
  >({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    passport?: { id: string; fileName: string };
    currentVisa?: { id: string; fileName: string };
    marriageCertificate?: { id: string; fileName: string };
  }>({});
  const postalService = createPostalLookupService();

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const personalTypes = ["PASSPORT", "CURRENT_VISA", "MARRIAGE_CERTIFICATE"];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(personalTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "passport",
        label: "Passport",
        description: "Clear copy of passport main page",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.passport,
        stage: "passport",
      },
      {
        id: "currentvisa",
        label: "Current Visa",
        description: "Current visa page or grant notice",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!values.hasCurrentVisa,
        uploaded: !!uploadedDocuments.currentVisa,
        stage: "visa",
      },
      {
        id: "marriagecertificate",
        label: "Marriage Certificate",
        description: "Certified copy of marriage certificate",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: values.maritalStatus === "Married",
        uploaded: !!uploadedDocuments.marriageCertificate,
        stage: "basic",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs =
            await documentService.fetchDocumentsWithMapping(personalTypes);

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const handleSameCurrentAddressChange = async (checked: boolean) => {
    setFieldValue("isHomeSameAsCurrent", checked);
    if (checked) {
      await setFieldValue(
        "homeAddressStreet1",
        values.currentAddressStreet1 || "",
      );
      await setFieldValue(
        "homeAddressStreet2",
        values.currentAddressStreet2 || "",
      );
      await setFieldValue("homeAddressCity", values.currentAddressCity || "");
      await setFieldValue("homeAddressState", values.currentAddressState || "");
      await setFieldValue(
        "homeAddressPostalCode",
        values.currentAddressPostalCode || "",
      );
      await setFieldValue(
        "homeAddressCountry",
        values.currentAddressCountry || "",
      );

      const newTouched = { ...touched };
      if (values.currentAddressStreet1) newTouched.homeAddressStreet1 = true;
      if (values.currentAddressCity) newTouched.homeAddressCity = true;
      if (values.currentAddressState) newTouched.homeAddressState = true;
      if (values.currentAddressPostalCode)
        newTouched.homeAddressPostalCode = true;
      if (values.currentAddressCountry) newTouched.homeAddressCountry = true;
      await setTouched(newTouched);
    } else if (!checked) {
      await setFieldValue("homeAddressStreet1", "");
      await setFieldValue("homeAddressStreet2", "");
      await setFieldValue("homeAddressCity", "");
      await setFieldValue("homeAddressState", "");
      await setFieldValue("homeAddressPostalCode", "");
      await setFieldValue("homeAddressCountry", "");

      const newTouched = { ...touched };
      delete newTouched.homeAddressStreet1;
      delete newTouched.homeAddressStreet2;
      delete newTouched.homeAddressCity;
      delete newTouched.homeAddressState;
      delete newTouched.homeAddressPostalCode;
      delete newTouched.homeAddressCountry;
      await setTouched(newTouched);
    }
  };

  const handleSameCountryOfCitizenshipChange = (checked: boolean) => {
    setFieldValue("isCitizenshipSameAsBirth", checked);
    if (checked && values.countryOfBirth) {
      setFieldValue("countryOfCitizenship", values.countryOfBirth);
    } else if (!checked) {
      setFieldValue("countryOfCitizenship", "");
    }
  };

  const handleSamePassportCountryChange = (checked: boolean) => {
    setFieldValue("isPassportSameAsBirth", checked);
    if (checked && values.countryOfBirth) {
      setFieldValue("passportIssuingCountry", values.countryOfBirth);
    } else if (!checked) {
      setFieldValue("passportIssuingCountry", "");
    }
  };

  const handleCountryOfBirthChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newCountry = e.target.value;
    handleChange(e);

    if (values.isCitizenshipSameAsBirth) {
      setFieldValue("countryOfCitizenship", newCountry);
    }
    if (values.isPassportSameAsBirth) {
      setFieldValue("passportIssuingCountry", newCountry);
    }
  };

  const createAddressHandler = (
    fieldPrefix: "currentAddress" | "homeAddress",
  ) => {
    return async (addressData: any) => {
      const prevPostalCode =
        fieldPrefix === "currentAddress"
          ? values.currentAddressPostalCode
          : values.homeAddressPostalCode;

      if (addressData.postalCode && addressData.country) {
        addressData.postalCode = formatPostalCode(
          addressData.postalCode,
          addressData.country,
        );
      }

      const prefix =
        fieldPrefix === "currentAddress" ? "currentAddress" : "homeAddress";
      setFieldValue(`${prefix}Street1`, addressData.street1 || "");
      setFieldValue(`${prefix}Street2`, addressData.street2 || "");
      setFieldValue(`${prefix}City`, addressData.city || "");
      setFieldValue(`${prefix}State`, addressData.state || "");
      setFieldValue(`${prefix}PostalCode`, addressData.postalCode || "");
      setFieldValue(`${prefix}Country`, addressData.country || "");

      const newTouched = { ...touched };
      newTouched[`${prefix}Street1`] = true;
      newTouched[`${prefix}City`] = true;
      newTouched[`${prefix}PostalCode`] = true;
      newTouched[`${prefix}Country`] = true;

      if (addressData.country && shouldShowAddressState(addressData.country)) {
        newTouched[`${prefix}State`] = true;
      }

      if (fieldPrefix === "currentAddress" && values.isHomeSameAsCurrent) {
        setFieldValue("homeAddressStreet1", addressData.street1 || "");
        setFieldValue("homeAddressStreet2", addressData.street2 || "");
        setFieldValue("homeAddressCity", addressData.city || "");
        setFieldValue("homeAddressState", addressData.state || "");
        setFieldValue("homeAddressPostalCode", addressData.postalCode || "");
        setFieldValue("homeAddressCountry", addressData.country || "");

        newTouched.homeAddressStreet1 = true;
        newTouched.homeAddressCity = true;
        newTouched.homeAddressPostalCode = true;
        newTouched.homeAddressCountry = true;

        if (
          addressData.country &&
          shouldShowAddressState(addressData.country)
        ) {
          newTouched.homeAddressState = true;
        }
      }

      await setTouched(newTouched);

      if (
        addressData.postalCode !== prevPostalCode &&
        addressData.postalCode &&
        addressData.country &&
        addressData.postalCode.length > 2
      ) {
        if (lookupTimeouts[fieldPrefix]) {
          clearTimeout(lookupTimeouts[fieldPrefix]);
        }

        const timeoutId = setTimeout(async () => {
          try {
            const result = await postalService.lookupByPostalCode(
              addressData.postalCode,
              addressData.country,
            );

            if (result.success && result.data) {
              const enhancedAddress = {
                street1: addressData.street1 || result.data.street1 || "",
                street2: addressData.street2 || result.data.street2 || "",
                city: addressData.city || result.data.city || "",
                state: addressData.state || result.data.state || "",
                postalCode: addressData.postalCode,
                country: addressData.country,
              };

              setFieldValue(`${prefix}Street1`, enhancedAddress.street1);
              setFieldValue(`${prefix}Street2`, enhancedAddress.street2);
              setFieldValue(`${prefix}City`, enhancedAddress.city);
              setFieldValue(`${prefix}State`, enhancedAddress.state);

              if (
                fieldPrefix === "currentAddress" &&
                values.isHomeSameAsCurrent
              ) {
                setFieldValue("homeAddressStreet1", enhancedAddress.street1);
                setFieldValue("homeAddressStreet2", enhancedAddress.street2);
                setFieldValue("homeAddressCity", enhancedAddress.city);
                setFieldValue("homeAddressState", enhancedAddress.state);
              }
            }
          } catch (error) {
            console.error("Address lookup failed:", error);
          }

          setLookupTimeouts((prev) => {
            const newTimeouts = { ...prev };
            delete newTimeouts[fieldPrefix];
            return newTimeouts;
          });
        }, 800);

        setLookupTimeouts((prev) => ({ ...prev, [fieldPrefix]: timeoutId }));
      }
    };
  };

  const handleCurrentAddressChange = createAddressHandler("currentAddress");
  const handleHomeAddressChange = createAddressHandler("homeAddress");

  const renderBasicDetailsSection = () => (
    <div className="basic-details-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Basic Details</h4>
        </div>
        <div className="form-fields">
          <GeneralSelectInput
            id="title"
            label="Title"
            options={titleOptions}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.title}
            error={errors.title}
            touched={touched.title}
          />
          <CheckBox
            label="I have a single name only"
            checked={values.isSingleNameOnly}
            onChange={(checked) => {
              setFieldValue("isSingleNameOnly", checked);
              if (checked) {
                setFieldValue("lastName", "");
                setFieldValue("middleName", "");
              }
            }}
          />
          <TextInput
            id="firstName"
            label="First Name *"
            placeholder="Enter first name"
            getFieldProps={() => ({
              value: values.firstName,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.firstName}
            touched={touched.firstName}
          />
          {!values.isSingleNameOnly && (
            <TextInput
              id="middleName"
              label="Middle Name"
              placeholder="Enter middle name"
              getFieldProps={() => ({
                value: values.middleName,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.middleName}
              touched={touched.middleName}
            />
          )}
          {!values.isSingleNameOnly && (
            <TextInput
              id="lastName"
              label="Last Name *"
              placeholder="Enter last name"
              getFieldProps={() => ({
                value: values.lastName,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.lastName}
              touched={touched.lastName}
            />
          )}
          <div className="date-of-birth-container">
            <DatePickerInput
              id="dateOfBirth"
              className="dateOfBirth"
              label="Date of Birth *"
              value={values.dateOfBirth || null}
              onChange={(date) => setFieldValue("dateOfBirth", date)}
              preset="pastDate"
            />
          </div>

          <MultiChoiceRadioButtonGroup
            name="gender"
            label="Gender *"
            options={genderOptions}
            value={values.gender as string}
            otherValue={values.customGender}
            onChange={(value) => setFieldValue("gender", value)}
            onOtherChange={(value) => {
              setFieldValue("customGender", value);
            }}
            showOther={true}
            otherPlaceholder="Enter gender"
          />
          <GeneralSelectInput
            id="countryOfBirth"
            label="Country of Birth *"
            options={countriesFlat}
            onChange={handleCountryOfBirthChange}
            onBlur={handleBlur}
            value={values.countryOfBirth}
            error={errors.countryOfBirth}
            touched={touched.countryOfBirth}
          />
          <GeneralSelectInput
            id="countryOfCitizenship"
            label="Country of Citizenship *"
            options={countriesFlat}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.countryOfCitizenship}
            error={errors.countryOfCitizenship}
            touched={touched.countryOfCitizenship}
          />
          <CheckBox
            label="Same as Country of Birth"
            checked={values.isCitizenshipSameAsBirth}
            onChange={handleSameCountryOfCitizenshipChange}
          />
          <GeneralSelectInput
            id="maritalStatus"
            label="Marital Status *"
            options={maritalStatusOptions}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.maritalStatus}
            error={errors.maritalStatus}
            touched={touched.maritalStatus}
          />
          {values.maritalStatus === "Married" && (
            <div className="marriage-date-container">
              <DatePickerInput
                id="registeredDateOfMarriage"
                className="registeredDateOfMarriage"
                label="Date of Marriage *"
                value={values.registeredDateOfMarriage || null}
                onChange={(date) =>
                  setFieldValue("registeredDateOfMarriage", date)
                }
                preset="pastDate"
              />
            </div>
          )}

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Marriage Certificate"
        description="Please attach a copy of your marriage certificate:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderContactDetailsSection = () => (
    <div className="contact-details-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Contact Details</h4>
        </div>

        <div className="form-fields">
          <EmailInput
            id="email"
            label="Email Address *"
            placeholder="Enter email"
            getFieldProps={() => ({
              value: values.email,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.email}
            touched={touched.email}
            disabled={true}
          />
          <PhoneNumberInput
            id="phone"
            label="Phone Number"
            value={values.phone}
            onChange={(phoneData) => {
              setFieldValue("phone", phoneData);
            }}
            onBlur={() => {
              setFieldTouched("phone.countryCode", true);
              setFieldTouched("phone.areaCode", true);
              setFieldTouched("phone.number", true);
              setFieldTouched("phone.extension", true);
            }}
            error={typeof errors.phone === "string" ? errors.phone : undefined}
            touched={{
              countryCode: touched.phone?.countryCode,
              areaCode: touched.phone?.areaCode,
              number: touched.phone?.number,
              extension: touched.phone?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />
          <PhoneNumberInput
            id="mobile"
            label="Mobile Number"
            value={values.mobile}
            onChange={(phoneData) => {
              setFieldValue("mobile", phoneData);
            }}
            onBlur={() => {
              setFieldTouched("mobile.countryCode", true);
              setFieldTouched("mobile.areaCode", true);
              setFieldTouched("mobile.number", true);
              setFieldTouched("mobile.extension", true);
            }}
            error={
              typeof errors.mobile === "string" ? errors.mobile : undefined
            }
            touched={{
              countryCode: touched.mobile?.countryCode,
              areaCode: touched.mobile?.areaCode,
              number: touched.mobile?.number,
              extension: touched.mobile?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />

          <AddressInput
            id="currentAddress"
            label="Current Address *"
            value={{
              street1: values.currentAddressStreet1,
              street2: values.currentAddressStreet2,
              city: values.currentAddressCity,
              state: values.currentAddressState,
              postalCode: values.currentAddressPostalCode,
              country: values.currentAddressCountry,
            }}
            onChange={handleCurrentAddressChange}
            onBlur={(fieldName: any) => {
              setFieldTouched(
                `currentAddress${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`,
                true,
              );
            }}
            countries={countriesFlat}
            errors={{
              street1: errors.currentAddressStreet1,
              street2: errors.currentAddressStreet2,
              city: errors.currentAddressCity,
              state: errors.currentAddressState,
              postalCode: errors.currentAddressPostalCode,
              country: errors.currentAddressCountry,
            }}
            touched={{
              street1: touched.currentAddressStreet1,
              street2: touched.currentAddressStreet2,
              city: touched.currentAddressCity,
              state: touched.currentAddressState,
              postalCode: touched.currentAddressPostalCode,
              country: touched.currentAddressCountry,
            }}
          />

          <AddressInput
            id="homeAddress"
            label="Permanent Address in Home Country *"
            value={{
              street1: values.homeAddressStreet1,
              street2: values.homeAddressStreet2,
              city: values.homeAddressCity,
              state: values.homeAddressState,
              postalCode: values.homeAddressPostalCode,
              country: values.homeAddressCountry,
            }}
            onChange={handleHomeAddressChange}
            onBlur={(fieldName: any) => {
              setFieldTouched(
                `homeAddress${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`,
                true,
              );
            }}
            countries={countriesFlat}
            errors={{
              street1: errors.homeAddressStreet1,
              street2: errors.homeAddressStreet2,
              city: errors.homeAddressCity,
              state: errors.homeAddressState,
              postalCode: errors.homeAddressPostalCode,
              country: errors.homeAddressCountry,
            }}
            touched={{
              street1: touched.homeAddressStreet1,
              street2: touched.homeAddressStreet2,
              city: touched.homeAddressCity,
              state: touched.homeAddressState,
              postalCode: touched.homeAddressPostalCode,
              country: touched.homeAddressCountry,
            }}
          />

          <CheckBox
            label="Same as Current Address"
            checked={values.isHomeSameAsCurrent}
            onChange={handleSameCurrentAddressChange}
          />
        </div>
      </div>
    </div>
  );

  const renderEmergencyContactSection = () => (
    <div className="emergency-contact-details-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Emergency Contact</h4>
        </div>

        <div className="form-fields">
          <TextInput
            id="emergencyContactName"
            label="Emergency Contact Name"
            placeholder="Enter emergency contact name"
            getFieldProps={() => ({
              value: values.emergencyContactName,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.emergencyContactName}
            touched={touched.emergencyContactName}
          />
          <TextInput
            id="emergencyContactRelationship"
            label="Relationship"
            placeholder="e.g., Parent, Spouse"
            getFieldProps={() => ({
              value: values.emergencyContactRelationship,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.emergencyContactRelationship}
            touched={touched.emergencyContactRelationship}
          />
          <PhoneNumberInput
            id="emergencyContactPhone"
            label="Emergency Contact Phone"
            value={values.emergencyContactPhone}
            onChange={(phoneData) => {
              setFieldValue("emergencyContactPhone", phoneData);
              setFieldTouched("emergencyContactPhone", true);
              setFieldTouched("emergencyContactMobile", true);
            }}
            onBlur={() => {
              setFieldTouched("emergencyContactPhone.countryCode", true);
              setFieldTouched("emergencyContactPhone.areaCode", true);
              setFieldTouched("emergencyContactPhone.number", true);
              setFieldTouched("emergencyContactPhone.extension", true);
            }}
            error={
              typeof errors.emergencyContactPhone === "string"
                ? errors.emergencyContactPhone
                : undefined
            }
            touched={{
              countryCode: touched.emergencyContactPhone?.countryCode,
              areaCode: touched.emergencyContactPhone?.areaCode,
              number: touched.emergencyContactPhone?.number,
              extension: touched.emergencyContactPhone?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />
          <PhoneNumberInput
            id="emergencyContactMobile"
            label="Emergency Contact Mobile"
            value={values.emergencyContactMobile}
            onChange={(phoneData) => {
              setFieldValue("emergencyContactMobile", phoneData);
              setFieldTouched("emergencyContactPhone", true);
              setFieldTouched("emergencyContactMobile", true);
            }}
            onBlur={() => {
              setFieldTouched("emergencyContactMobile.countryCode", true);
              setFieldTouched("emergencyContactMobile.areaCode", true);
              setFieldTouched("emergencyContactMobile.number", true);
              setFieldTouched("emergencyContactMobile.extension", true);
            }}
            error={
              typeof errors.emergencyContactMobile === "string"
                ? errors.emergencyContactMobile
                : undefined
            }
            touched={{
              countryCode: touched.emergencyContactMobile?.countryCode,
              areaCode: touched.emergencyContactMobile?.areaCode,
              number: touched.emergencyContactMobile?.number,
              extension: touched.emergencyContactMobile?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />
        </div>
      </div>
    </div>
  );

  const renderPassportDetailsSection = () => (
    <div className="passport-details-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Passport Details</h4>
        </div>

        <div className="form-fields">
          <TextInput
            id="passportNumber"
            label="Passport Number"
            placeholder="Enter passport number"
            getFieldProps={() => ({
              value: values.passportNumber,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.passportNumber}
            touched={touched.passportNumber}
          />
          <GeneralSelectInput
            id="passportIssuingCountry"
            label="Issuing Country"
            options={countriesFlat}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.passportIssuingCountry}
            error={errors.passportIssuingCountry}
            touched={touched.passportIssuingCountry}
          />
          <CheckBox
            label="Same as Country of Birth"
            checked={values.isPassportSameAsBirth}
            onChange={handleSamePassportCountryChange}
          />
          <div className="passport-expiry-container">
            <DatePickerInput
              id="passportExpiryDate"
              className="passportExpiryDate"
              label="Passport Expiry Date"
              value={values.passportExpiryDate || null}
              onChange={(date) => setFieldValue("passportExpiryDate", date)}
              preset="futureDate"
            />
          </div>

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Passport Documents"
        description="Please attach a copy of your passport page:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderVisaDetailsSection = () => (
    <div className="visa-details-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Visa Details</h4>
        </div>

        <div className="form-fields">
          <RadioButtonGroup
            name="isCurrentlyInCountry"
            label={`Are you currently in ${destinationCountry}? *`}
            value={values.isCurrentlyInCountry}
            onChange={(value) => {
              setFieldValue("isCurrentlyInCountry", value);
              setFieldTouched("isCurrentlyInCountry", true);

              if (value === false) {
                setFieldValue("yearOfFirstArrival", null);
              }
            }}
          />
          {values.isCurrentlyInCountry && (
            <div className="year-of-first-arrival-container">
              <YearPickerInput
                id="yearOfFirstArrival"
                className="yearOfFirstArrival"
                label="Year of First Arrival *"
                value={values.yearOfFirstArrival}
                onChange={(date) => setFieldValue("yearOfFirstArrival", date)}
              />
            </div>
          )}
          <RadioButtonGroup
            name="hasCurrentVisa"
            label={`Do you currently hold a visa for ${destinationCountry}? *`}
            value={values.hasCurrentVisa}
            onChange={(value) => {
              setFieldValue("hasCurrentVisa", value);
              setFieldTouched("hasCurrentVisa", true);

              if (value === true) {
                setFieldValue("isApplyStudentVisa", null);
                setFieldValue("applicationType", null);
                setFieldValue("applicationCountry", null);
              }

              if (value === false) {
                setFieldValue("currentVisaType", null);
                setFieldValue("visaExpiryDate", null);
              }
            }}
          />
          {values.hasCurrentVisa && (
            <>
              <MultiChoiceRadioButtonGroup
                name="currentVisaType"
                label="Current visa type *"
                options={visaTypeOptions}
                value={values.currentVisaType as string}
                onChange={(value) => setFieldValue("currentVisaType", value)}
                showOther={true}
                otherPlaceholder="Enter visa type."
              />
              <div className="visa-expiry-container">
                <DatePickerInput
                  id="visaExpiryDate"
                  className="visaExpiryDate"
                  label="Visa Expiry Date *"
                  value={values.visaExpiryDate || null}
                  onChange={(date) => setFieldValue("visaExpiryDate", date)}
                />
              </div>
            </>
          )}

          {!values.hasCurrentVisa && values.hasCurrentVisa !== null && (
            <>
              <RadioButtonGroup
                name="isApplyStudentVisa"
                label={`Will you be applying for a student visa? *`}
                value={values.isApplyStudentVisa}
                onChange={(value) => {
                  setFieldValue("isApplyStudentVisa", value);
                  setFieldTouched("isApplyStudentVisa", true);
                }}
              />
              {values.isApplyStudentVisa && (
                <>
                  <MultiChoiceRadioButtonGroup
                    name="applicationType"
                    label="Application type *"
                    options={applicationTypeOptions}
                    value={values.applicationType as string}
                    onChange={(value) => {
                      setFieldValue("applicationType", value);
                      setFieldTouched("applicationType", true);

                      if (value === "onshore") {
                        setFieldValue("applicationCountry", null);
                      }
                    }}
                  />
                  {values.applicationType === "offshore" && (
                    <GeneralSelectInput
                      id="applicationCountry"
                      label="Which country will you apply from? *"
                      options={countriesFlat}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.applicationCountry}
                      error={errors.applicationCountry}
                      touched={touched.applicationCountry}
                    />
                  )}
                </>
              )}
            </>
          )}
          <RadioButtonGroup
            name="hasEntryRefusal"
            label={`Have you or any dependent ever been refused entry to ${destinationCountry} or had a visa cancelled? *`}
            value={values.hasEntryRefusal}
            onChange={(value) => {
              setFieldValue("hasEntryRefusal", value);
              setFieldTouched("hasEntryRefusal", true);
            }}
          />
          {values.hasEntryRefusal && (
            <MessageTextAreaInput
              id={`entryRefusalDetails`}
              label="Please provide details *"
              placeholder="Please provide details"
              rows="4"
              maxLength={500}
              getFieldProps={() => ({
                value: values.entryRefusalDetails,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.entryRefusalDetails}
              touched={touched.entryRefusalDetails}
            />
          )}

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Visa Documents"
        description="Please attach a copy of your visa page:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  switch (currentSubStage) {
    case "basic":
      return renderBasicDetailsSection();
    case "contact":
      return renderContactDetailsSection();
    case "emergency":
      return renderEmergencyContactSection();
    case "passport":
      return renderPassportDetailsSection();
    case "visa":
      return renderVisaDetailsSection();
    default:
      return renderBasicDetailsSection();
  }
};

export default AppPersonalSection;
